import * as THREE from 'three';
import { v4 as uuid } from 'uuid';
import { ClipTask, DefaultColors } from '../defines';

import { GradientType } from '../materials/Gradients';
import { Color } from 'three';
import { createDepthFadeMeshMaterial } from '../materials/DepthFadeUtils';
import type { Subscription, ReactiveProperty } from '@pointorama/potree-shared';
import { getAnnotationVisibleProperty, getAnnotationColorProperty, getPlaneHeatmapGradientProperty, getPlaneHeatmapIsolateProperty, getPlaneToleranceProperty, getFilterToggleProperty, getClipMethodProperty, getPlanePositionProperty, getPlaneNormalProperty, getPlaneRadiusProperty } from '@pointorama/potree-shared';

type WeightedPoint = {
  point: THREE.Vector3;
  weight: number;
};

export type ConstructorArgs = {
  identifier?: string;
  visible?: boolean;
  finished?: boolean;
  selected?: boolean;
  position?: THREE.Vector3;
  normal?: THREE.Vector3;
  clipTask?: number;
  tolerance?: number;
  radius?: number;
  gradient?: GradientType;
  heatmapIsolate?: boolean;
  color?: Color;
  /** When true, this plane uses intersection mode (must be inside ALL intersection planes).
   *  When false (default), uses union mode (must be inside at least ONE union plane). */
  useIntersection?: boolean;
};

export class PointoramaPlane extends THREE.Object3D {
  identifier: string;
  circle: THREE.Mesh;
  fullCircle: THREE.Mesh;

  finished = false;
  selected = false;
  snapped = false;
  depthFadeEnabled = false;

  bearing = 0;
  inclination = 0;
  normal = new THREE.Vector3();
  assignedClipTask: number;
  private _actualClipTask: number;
  tolerance: number;
  radius: number;
  thickness = 0.01;
  gradient: GradientType = 'SPECTRAL';
  heatmapIsolate = false;
  /** @deprecated Use color getter/setter instead — kept for backwards compat */
  frameColor: Color = DefaultColors.OBJECTS_THREE;
  /** When true, this plane uses intersection mode (must be inside ALL intersection planes).
   *  When false (default), uses union mode (must be inside at least ONE union plane). */
  useIntersection = false;

  // Reactive property subscriptions
  private _visibleSubscription?: Subscription;
  private _colorProperty?: ReactiveProperty<string>;
  private _frameColorSubscription?: Subscription;
  private _heatmapGradientSubscription?: Subscription;
  private _heatmapIsolateSubscription?: Subscription;
  private _filterToggleSubscription?: Subscription;
  private _clipMethodSubscription?: Subscription;
  private _toleranceSubscription?: Subscription;
  private _positionSubscription?: Subscription;
  private _normalSubscription?: Subscription;
  private _radiusSubscription?: Subscription;

  // Filter state (kept in sync by reactive subscriptions)
  private _filterEnabled = false;
  private _clipMethod = 'ISOLATE';

  // RANSAC parameters - exposed for configuration
  public maxIterations = 100;
  public outlierThreshold = 0.00001; // Distance threshold in meters
  public minPointsRatio = 0.4; // Keep at least 40% of points

  constructor(args: ConstructorArgs = {}) {
    super();

    this.identifier = args.identifier || uuid();
    this.visible = args.visible === undefined ? true : args.visible;
    if (args.position && args.normal) {
      this.position.set(args.position.x, args.position.y, args.position.z);
      this.setRotationFromNormal(args.normal);
      this.calculateInclinationAndBearing();
    }
    this.assignedClipTask = args.clipTask || ClipTask.NONE;
    this._actualClipTask = this.assignedClipTask;
    this.tolerance = args.tolerance || 0.01;
    this.radius = args.radius || 1.0;
    this.gradient = args.gradient || 'SPECTRAL';
    this.heatmapIsolate = args.heatmapIsolate || false;
    this.frameColor = args.color || DefaultColors.OBJECTS_THREE;
    this.useIntersection = args.useIntersection || false;

    // Marker — uses depth fade shader for smooth fading behind point clouds
    const markerMaterial = createDepthFadeMeshMaterial(this.frameColor);

    const markerGeometry = new THREE.TorusGeometry(this.radius, this.thickness * this.radius, 16, 100);
    this.circle = new THREE.Mesh(markerGeometry, markerMaterial);
    this.add(this.circle);

    // Selectable inside
    const fullCircleGeometry = new THREE.CircleGeometry(this.radius, 32);
    const fullCircleMaterial = new THREE.MeshBasicMaterial({
      visible: false,
      side: THREE.DoubleSide,
      depthTest: false,
      depthWrite: false,
    });
    this.fullCircle = new THREE.Mesh(fullCircleGeometry, fullCircleMaterial);
    this.add(this.fullCircle);

    if (args.finished) this.finish();

    if (args.selected) this.select();
    else this.deselect();

    this.loadReactiveProperties(args);
  }

  /**
   * Subscribe to reactive properties.
   * For each property: if the corresponding constructor arg was provided, write it to the property.
   * Otherwise, read the existing property value (cached from a previous session or registry default).
   */
  loadReactiveProperties(args: Partial<ConstructorArgs> = {}): void {
    if (!this.identifier) return;

    try {
      this._visibleSubscription?.unsubscribe();
      this._frameColorSubscription?.unsubscribe();
      this._heatmapGradientSubscription?.unsubscribe();
      this._heatmapIsolateSubscription?.unsubscribe();
      this._toleranceSubscription?.unsubscribe();
      this._filterToggleSubscription?.unsubscribe();
      this._clipMethodSubscription?.unsubscribe();

      // --- Visible ---
      const visibleProperty = getAnnotationVisibleProperty(this.identifier);
      if (args.visible !== undefined) visibleProperty.value = this.visible;
      else this.visible = visibleProperty.value;
      this._visibleSubscription = visibleProperty.subscribeToChanges((value) => {
        this.visible = value;
      });

      // --- Object color (annotation_color) ---
      this._colorProperty = getAnnotationColorProperty(this.identifier) as ReactiveProperty<string>;
      if (args.color !== undefined) this._colorProperty.value = '#' + this.color.getHexString();
      else this.color = new Color(this._colorProperty.value);
      this._frameColorSubscription = this._colorProperty.subscribeToChanges((value: string) => {
        this.color = new Color(value);
      });

      // --- Heatmap gradient ---
      const gradientProp = getPlaneHeatmapGradientProperty(this.identifier);
      if (args.gradient !== undefined) gradientProp.value = this.gradient;
      else this.gradient = gradientProp.value as GradientType;
      this._heatmapGradientSubscription = gradientProp.subscribeToChanges((value) => {
        this.gradient = value as GradientType;
      });

      // --- Heatmap isolate ---
      const isolateProp = getPlaneHeatmapIsolateProperty(this.identifier);
      if (args.heatmapIsolate !== undefined) isolateProp.value = this.heatmapIsolate;
      else this.heatmapIsolate = isolateProp.value;
      this._heatmapIsolateSubscription = isolateProp.subscribeToChanges((value) => {
        this.heatmapIsolate = value;
      });

      // --- Tolerance ---
      const toleranceProp = getPlaneToleranceProperty(this.identifier);
      if (args.tolerance !== undefined) toleranceProp.value = this.tolerance;
      else this.tolerance = toleranceProp.value;
      this._toleranceSubscription = toleranceProp.subscribeToChanges((value) => {
        this.tolerance = value;
      });

      // --- Filter toggle + clip method (always read — managed by UI, not constructor) ---
      const toggleProperty = getFilterToggleProperty(this.identifier);
      if (toggleProperty) {
        this._filterToggleSubscription = toggleProperty.subscribeToChanges((enabled) => {
          this._filterEnabled = enabled;
          this._updateClipTask();
        });
        this._filterEnabled = toggleProperty.value;
      }

      const clipMethodProperty = getClipMethodProperty(this.identifier);
      if (clipMethodProperty) {
        this._clipMethodSubscription = clipMethodProperty.subscribeToChanges((method) => {
          this._clipMethod = method;
          this._updateClipTask();
        });
        this._clipMethod = clipMethodProperty.value;
      }

      // Geometry properties (position, normal, radius)
      // Only skip initial emission if the plane already has non-default geometry
      const hasPosition = this.position.x !== 0 || this.position.y !== 0 || this.position.z !== 0;
      const hasRadius = this.radius !== 1;

      this._positionSubscription?.unsubscribe();
      const posProp = getPlanePositionProperty(this.identifier);
      let posInit = false;
      this._positionSubscription = posProp.subscribe((value) => {
        if (hasPosition && !posInit) { posInit = true; return; }
        posInit = true;
        this.position.set(value.x, value.y, value.z);
      });

      this._normalSubscription?.unsubscribe();
      const normalProp = getPlaneNormalProperty(this.identifier);
      let normalInit = false;
      this._normalSubscription = normalProp.subscribe((value) => {
        if (hasPosition && !normalInit) { normalInit = true; return; }
        normalInit = true;
        this.setRotationFromNormal(new THREE.Vector3(value.x, value.y, value.z));
      });

      this._radiusSubscription?.unsubscribe();
      const radiusProp = getPlaneRadiusProperty(this.identifier);
      let radiusInit = false;
      this._radiusSubscription = radiusProp.subscribe((value) => {
        if (hasRadius && !radiusInit) { radiusInit = true; return; }
        radiusInit = true;
        this.radius = value;
      });
    } catch {
      // Global property service not initialized yet
    }
  }

  private _updateClipTask() {
    this.assignedClipTask = this._filterEnabled ? this._clipMethodToTask(this._clipMethod) : ClipTask.NONE;
  }

  private _clipMethodToTask(clipMethod: string): number {
    switch (clipMethod) {
      case 'HIGHLIGHT':
        return ClipTask.GRAYSCALE;
      case 'HIDE':
        return ClipTask.SHOW_OUTSIDE;
      case 'ISOLATE':
        return ClipTask.SHOW_INSIDE;
      case 'HEATMAP':
        return ClipTask.HEATMAP;
      default:
        return ClipTask.NONE;
    }
  }

  dispose(): void {
    this._visibleSubscription?.unsubscribe();
    this._frameColorSubscription?.unsubscribe();
    this._heatmapGradientSubscription?.unsubscribe();
    this._heatmapIsolateSubscription?.unsubscribe();
    this._toleranceSubscription?.unsubscribe();
    this._filterToggleSubscription?.unsubscribe();
    this._clipMethodSubscription?.unsubscribe();
  }

  setRadius(radius: number) {
    // Skip if radius hasn't changed to avoid expensive geometry recreation every frame
    if (Math.abs(this.radius - radius) < 0.0001) {
      return;
    }

    this.circle.geometry.dispose();
    // Increase thickness proportionally with radius - larger planes get thicker borders
    const effectiveThickness = this.thickness * radius * 4;
    this.circle.geometry = new THREE.TorusGeometry(radius, effectiveThickness, 16, 100);
    this.fullCircle.geometry.dispose();
    this.fullCircle.geometry = new THREE.CircleGeometry(radius, 32);
    this.radius = radius;
  }

  setThickness(thickness: number) {
    this.thickness = thickness;
    this.circle.geometry.dispose();
    this.circle.geometry = new THREE.TorusGeometry(this.radius, this.thickness * this.radius, 16, 100);
  }

  set color(color: THREE.Color) {
    this.frameColor = color;
    const mat = this.circle.material as THREE.ShaderMaterial;
    if (mat.uniforms?.uColor) {
      mat.uniforms.uColor.value = color;
    }
  }
  get color(): THREE.Color {
    const mat = this.circle.material as THREE.ShaderMaterial;
    return mat.uniforms?.uColor?.value ?? this.frameColor;
  }

  get annotationColor(): string {
    return this._colorProperty?.value ?? '#' + this.color.getHexString();
  }

  set annotationColor(value: string) {
    this.color = new Color(value);
    if (this._colorProperty) {
      this._colorProperty.value = value;
    }
  }

  defineFromOtherPlane(otherPlane: PointoramaPlane) {
    this.setRotationFromNormal(otherPlane.getNormal());
    this.position.set(otherPlane.position.x, otherPlane.position.y, otherPlane.position.z);
    this.bearing = otherPlane.bearing;
    this.inclination = otherPlane.inclination;
  }

  setRotationFromNormal(normal: THREE.Vector3) {
    this.normal = normal;
    const quaternion = new THREE.Quaternion();
    quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), normal);
    this.setRotationFromQuaternion(quaternion);
  }

  setRotationFromPoints(points: THREE.Vector3[] | WeightedPoint[], cameraPosition?: THREE.Vector3) {
    // Handle both weighted and unweighted points
    const weightedPoints: WeightedPoint[] = points.length > 0 && points[0] instanceof THREE.Vector3 ? (points as THREE.Vector3[]).map((p) => ({ point: p, weight: 1.0 })) : (points as WeightedPoint[]);

    if (weightedPoints.length < 3) return;

    // Recursive outlier removal parameters
    const maxIterations = this.maxIterations;
    const outlierThreshold = this.outlierThreshold;
    const minPointsRatio = this.minPointsRatio;
    const minPoints = Math.max(3, Math.floor(weightedPoints.length * minPointsRatio));

    const result = this.fitPlaneWithOutlierRemoval(weightedPoints, maxIterations, outlierThreshold, minPoints, cameraPosition);

    if (result) {
      this.setRotationFromNormal(result.normal);
      this.calculateInclinationAndBearing();
      this.snapped = false; // Reset snap state when recalculating from points
    }
  }

  private fitPlaneWithOutlierRemoval(weightedPoints: WeightedPoint[], maxIterations: number, outlierThreshold: number, minPoints: number, cameraPosition?: THREE.Vector3): { normal: THREE.Vector3; centroid: THREE.Vector3 } | null {
    // Use RANSAC for robust plane fitting
    if (weightedPoints.length < 3) return null;

    let bestInliers: WeightedPoint[] = [];
    let bestScore = -Infinity;

    for (let iteration = 0; iteration < maxIterations; iteration++) {
      // 1. Randomly sample 3 points to define a plane
      const sampleIndices = this.getRandomSample(weightedPoints.length, 3);
      const samplePoints = sampleIndices.map((i) => weightedPoints[i]);

      // 2. Fit plane to sample
      const candidatePlane = this.fitPlaneToPoints(samplePoints);
      if (!candidatePlane) continue;

      // 3. Find all inliers (points within threshold distance)
      const inliers: WeightedPoint[] = [];
      let totalWeight = 0;

      for (const wp of weightedPoints) {
        const distance = Math.abs(wp.point.clone().sub(candidatePlane.centroid).dot(candidatePlane.normal));
        if (distance <= outlierThreshold) {
          inliers.push(wp);
          totalWeight += wp.weight;
        }
      }

      // 4. Score based on number of inliers and their weights
      const score = inliers.length * Math.sqrt(totalWeight);

      // 5. Keep best model
      if (score > bestScore && inliers.length >= minPoints) {
        bestScore = score;
        bestInliers = inliers;
      }
    }

    // 6. Refit plane using all inliers for final result
    if (bestInliers.length >= minPoints) {
      return this.fitPlaneToPoints(bestInliers, cameraPosition);
    }

    // Fallback: use statistical outlier removal with MAD
    return this.fitPlaneWithMADOutlierRemoval(weightedPoints, outlierThreshold, minPoints, cameraPosition);
  }

  private getRandomSample(arrayLength: number, sampleSize: number): number[] {
    const indices: number[] = [];
    const used = new Set<number>();

    while (indices.length < sampleSize && indices.length < arrayLength) {
      const idx = Math.floor(Math.random() * arrayLength);
      if (!used.has(idx)) {
        used.add(idx);
        indices.push(idx);
      }
    }

    return indices;
  }

  private fitPlaneWithMADOutlierRemoval(weightedPoints: WeightedPoint[], outlierThreshold: number, minPoints: number, cameraPosition?: THREE.Vector3): { normal: THREE.Vector3; centroid: THREE.Vector3 } | null {
    // Initial plane fit
    const initialPlane = this.fitPlaneToPoints(weightedPoints, cameraPosition);
    if (!initialPlane) return null;

    // Calculate distances from plane
    const distances = weightedPoints.map((wp) => Math.abs(wp.point.clone().sub(initialPlane.centroid).dot(initialPlane.normal)));

    // Calculate median and MAD
    const sortedDistances = [...distances].sort((a, b) => a - b);
    const median = sortedDistances[Math.floor(sortedDistances.length / 2)];

    const absoluteDeviations = distances.map((d) => Math.abs(d - median));
    const sortedDeviations = [...absoluteDeviations].sort((a, b) => a - b);
    const mad = sortedDeviations[Math.floor(sortedDeviations.length / 2)];

    // Modified Z-score using MAD
    const madMultiplier = 1.4826; // Constant to make MAD comparable to standard deviation
    const threshold = Math.max(outlierThreshold, median + 3 * mad * madMultiplier);

    // Filter inliers
    const inliers = weightedPoints.filter((_, i) => distances[i] <= threshold);

    if (inliers.length >= minPoints) {
      return this.fitPlaneToPoints(inliers, cameraPosition);
    }

    return initialPlane;
  }

  private fitPlaneToPoints(weightedPoints: WeightedPoint[], cameraPosition?: THREE.Vector3): { normal: THREE.Vector3; centroid: THREE.Vector3 } | null {
    if (weightedPoints.length < 3) return null;

    // 1. Calculate the weighted centroid
    const centroid = new THREE.Vector3();
    let totalWeight = 0;
    weightedPoints.forEach((wp) => {
      const weighted = wp.point.clone().multiplyScalar(wp.weight);
      centroid.add(weighted);
      totalWeight += wp.weight;
    });
    centroid.divideScalar(totalWeight);

    // 2. Calculate weighted covariance matrix
    let xx = 0,
      xy = 0,
      xz = 0;
    let yy = 0,
      yz = 0,
      zz = 0;

    weightedPoints.forEach((wp) => {
      const p = wp.point.clone().sub(centroid);
      const w = wp.weight;
      xx += w * p.x * p.x;
      xy += w * p.x * p.y;
      xz += w * p.x * p.z;
      yy += w * p.y * p.y;
      yz += w * p.y * p.z;
      zz += w * p.z * p.z;
    });

    const covarianceMatrix = [
      [xx, xy, xz],
      [xy, yy, yz],
      [xz, yz, zz],
    ];

    // 3. Find the smallest eigenvector
    let result: { lambda: { x: number[] }; E: { x: number[][] } };
    try {
      result = numeric.eig(covarianceMatrix, 1000);
    } catch {
      return null;
    }
    const eigenvalues = result.lambda.x;
    const eigenvectors = result.E.x;

    let minIndex = 0;
    for (let i = 1; i < eigenvalues.length; i++) {
      if (eigenvalues[i] < eigenvalues[minIndex]) {
        minIndex = i;
      }
    }

    // 4. Find the normal vector of the plane
    const normal = new THREE.Vector3(eigenvectors[0][minIndex], eigenvectors[1][minIndex], eigenvectors[2][minIndex]);

    // Orient normal to point towards camera if camera position is provided
    if (cameraPosition) {
      const toCamera = cameraPosition.clone().sub(centroid);
      if (normal.dot(toCamera) < 0) {
        normal.negate();
      }
    } else {
      // Fallback: ensure normal points upward
      if (normal.z < 0) {
        normal.negate();
      }
    }

    return { normal, centroid };
  }

  calculateNormal() {
    const x = -Math.sin(this.inclination) * Math.sin(this.bearing);
    const y = -Math.sin(this.inclination) * Math.cos(this.bearing);
    const z = Math.cos(this.inclination);

    this.normal = new THREE.Vector3(x, y, z).normalize();
  }

  calculateInclinationAndBearing() {
    const zAxis = new THREE.Vector3(0, 0, 1);

    this.inclination = this.normal.angleTo(zAxis);

    if (this.inclination === 0) {
      this.bearing = 0;
    } else {
      const yAxis = new THREE.Vector3(0, 1, 0);
      const bearingVector = this.normal.clone().negate();
      bearingVector.z = 0;

      this.bearing = bearingVector.angleTo(yAxis);

      const crossZ = yAxis.clone().cross(bearingVector).z;
      if (crossZ > 0) {
        this.bearing = 2 * Math.PI - this.bearing; // Adjust for clockwise direction
      }
    }
  }

  setInclinationDegrees(inclination: number) {
    this.inclination = (inclination * Math.PI) / 180;
    this.calculateNormal();
    this.setRotationFromNormal(this.normal);
  }

  setBearingDegrees(bearing: number) {
    this.bearing = (bearing * Math.PI) / 180;
    this.calculateNormal();
    this.setRotationFromNormal(this.normal);
  }

  snapInclination() {
    const inclination = this.getInclinationDegrees();
    const snappedNormal = new THREE.Vector3();
    if (inclination < 45) {
      snappedNormal.z = 1;
    } else {
      snappedNormal.x = this.normal.x;
      snappedNormal.y = this.normal.y;
      snappedNormal.z = 0;
      snappedNormal.normalize();
    }
    this.setRotationFromNormal(snappedNormal);
    this.calculateInclinationAndBearing();
    this.snapped = true;
  }

  getNormal() {
    return this.normal.clone().normalize();
  }

  getInclinationDegrees() {
    return this.inclination * (180 / Math.PI);
  }

  getBearingDegrees() {
    return this.bearing * (180 / Math.PI);
  }

  setDepthTest(alwaysOnTop: boolean) {
    const mat = this.circle.material as THREE.Material;
    mat.depthTest = !alwaysOnTop;
    mat.depthWrite = !alwaysOnTop;
    mat.needsUpdate = true;
  }

  finish() {
    this.setDepthTest(false);
    this.finished = true;
    this.depthFadeEnabled = true;
  }

  select() {
    this.selected = true;
    this.thickness = 0.03;
    this.circle.geometry.dispose();
    this.circle.geometry = new THREE.TorusGeometry(this.radius, this.thickness * this.radius, 16, 100);
  }

  deselect() {
    this.selected = false;
    this.thickness = 0.01;
    this.circle.geometry.dispose();
    this.circle.geometry = new THREE.TorusGeometry(this.radius, this.thickness * this.radius, 16, 100);
  }

  get actualClipTask(): number {
    if (this.assignedClipTask === ClipTask.NONE) return this._actualClipTask;
    return this.assignedClipTask;
  }

  set actualClipTask(value: number) {
    this._actualClipTask = value;
  }

  highlight() {
    if (this.assignedClipTask === ClipTask.NONE) {
      this._actualClipTask = ClipTask.HIGHLIGHT;
    }
  }

  unHighlight() {
    this._actualClipTask = this.assignedClipTask;
  }

  pointInsideClipArea(point: THREE.Vector3) {
    const planeConstant = -this.normal.dot(this.position);
    const plane = new THREE.Plane(this.normal, planeConstant);

    const distance = plane.distanceToPoint(point);

    return Math.abs(distance) <= this.tolerance;
  }

  fill(enable: boolean, props: { color?: THREE.Color; opacity?: number } = { color: DefaultColors.HIGHLIGHT, opacity: 0 }) {
    const material = this.fullCircle.material as THREE.MeshBasicMaterial;
    material.visible = enable;
    material.side = THREE.DoubleSide;
    material.color = props.color ?? material.color;
    material.opacity = props.opacity ?? material.opacity;
    material.transparent = material.opacity !== 0;
    material.needsUpdate = true;
  }

  getPointProjection(point: THREE.Vector3) {
    const n = this.getNormal();
    const v = point.clone().sub(this.position);
    const distance = v.dot(n);
    return point.clone().sub(n.multiplyScalar(distance));
  }

  getAngleBetweenPlanes(otherPlane: PointoramaPlane) {
    return Math.abs(Math.acos(Math.abs(this.getNormal().dot(otherPlane.getNormal()))));
  }

  containsPoint(point: THREE.Vector3) {
    const distance = this.getNormal().dot(point.clone().sub(this.position));
    return Math.abs(distance) <= 0.000001; // 1µm
  }
}
